/**
 * The marker button's state, as one pure total function.
 *
 * It lives here, tested, because this control used to get STUCK. It was inferred from streaming
 * signals with a latch bridging them, and the combinations had a hole: `baseLod` is pinned to 0
 * whenever far rings are off (Tiny/Short), so zooming out could never report "zoomed out" while
 * the marker's lod-0 chunk had stopped drawing — leaving a spinner with no way back.
 *
 * The rules, in order:
 *   1. Playable wins. Whatever the camera is doing, if the spawn gate is open, offer Play.
 *   2. Otherwise zoomed out → offer the zoom-in. "Zoomed out" is `baseLod > 0` (the coarse rings
 *      are what's drawing) OR simply being past the play range — the distance term is the
 *      backstop, because orbit distance is the one input no configuration can pin.
 *   3. Otherwise we are in range and not yet playable: loading.
 *
 * WHY IT CANNOT STICK: no latch, no timer, no history — the answer is a function of three values
 * recomputed every frame, and rule 2's distance term means the user's own zoom-out always
 * escapes `loading`, whatever the streamer is doing.
 */

export type MarkerButtonState = 'play' | 'zoom' | 'loading';

/**
 * Orbit distance beyond which the zoom-in affordance is offered (metres). Comfortably above the
 * 22 m the zoom-in lands at, so settling nudges don't flip the icon, and far below the 140 m
 * base-LOD-0 zoom ceiling.
 */
export const PLAY_RANGE_DISTANCE = 40;

export function markerButtonState(
  /** Displayed base LOD: 0 = lod-0 chunks are what's on screen, > 0 = coarse rings. */
  baseLod: number,
  /** Orbit distance in metres — the zoom tween's TARGET while one is in flight (so tapping
   *  zoom-in reads as in-range immediately, which is what the spinner-on-tap needs). */
  distance: number,
  /** The spawn gate: lod-0 resident + meshed + actually drawing at the marker. */
  canPlay: boolean,
): MarkerButtonState {
  if (canPlay) return 'play';
  if (baseLod > 0 || distance > PLAY_RANGE_DISTANCE) return 'zoom';
  return 'loading';
}
